import { TaskFromInsights, TasksInsights, TaskInsightsResult, InsightsSubtaskMeta } from 'redux/actions/tasks_v2'
import { IConfigGroup, ITag } from 'constants/interfaces'
import { countSubtaskStatuses, deriveTaskStatus } from 'utils/taskStatusModel'

// Result statuses constants
export const RESULT_STATUSES = {
  INPROGRESS: 1,
  DONE: 2,
  SKIPPED: -3,
  DISAPPROVED: 3,
  APPROVED: 4,
  APPROVED_WITH_RESERVATION: 5,
  REVIEWED_BY_AI: -5,
} as const

// Summary statuses
export const SUMMARY_STATUSES = {
  TODO: 'TODO',
  IN_PROGRESS: 'IN_PROGRESS',
  DONE: 'DONE',
} as const

const VM_TASK_TYPE = 2
const MAX_QUALITY_SCORE = 100

// A `sub_tasks` value is normally an object with title + metadata (`{ title, type, category }`),
// but may be a plain string in legacy data. Normalize to the full meta shape so consumers can
// read title / type / category safely regardless of the server format.
export const getInsightsSubtaskMeta = (
  value: InsightsSubtaskMeta | string | undefined,
  fallbackTitle = ''
): Required<Pick<InsightsSubtaskMeta, 'title'>> & Pick<InsightsSubtaskMeta, 'type' | 'category'> => {
  if (typeof value === 'string') return { title: value }
  if (value && typeof value === 'object') {
    return { title: value.title ?? fallbackTitle, type: value.type, category: value.category }
  }
  return { title: fallbackTitle }
}

// Convenience wrapper for the common case where only the title is needed.
export const getInsightsSubtaskTitle = (value: InsightsSubtaskMeta | string | undefined, fallback = ''): string =>
  getInsightsSubtaskMeta(value, fallback).title

const REWORK_QUALITY_PENALTY = 10
const LATE_QUALITY_PENALTY = 20

export type SummaryStatus = typeof SUMMARY_STATUSES[keyof typeof SUMMARY_STATUSES]

// Types for analytics
export interface TaskAnalytics {
  /**
   * Task/context-level status distribution — exactly the 5 statuses of the task status model
   * (`utils/taskStatusModel`). Approved-with-reservation and AI-reviewed are NOT task
   * statuses: they fold into `approved`. Their per-subtask counts stay available in
   * `reviewed` / `contextTableData` / the subtask+category breakdowns.
   */
  summary: {
    todo: number
    inProgress: number
    done: number
    approved: number
    rejected: number
    total: number
  }
  completion: {
    done: number
    inProgress: number
    todo: number
    skipped: number
    approved: number
    disapproved: number
    blocked: number
    total: number
  }
  skippedRanking: Array<{
    subtaskId: string
    subtaskTitle: string
    skippedCount: number
    contexts: Array<{
      contextId: string
      taskId: string
      taskTitle: string
      skippedAt: number // timestamp when skipped
      deadline: string // task deadline
    }>
  }>
  completedInTime: {
    total: number
    completedInTime: number
    completedLate: number
    percentage: number
  }
  storeCompletedInTime: {
    total: number
    completedInTime: number
    completedLate: number
    percentage: number
  }
  rating: {
    averageAll: number
    averageRatedOnly: number
    totalRatings: number
    ratingDistribution: Record<number, number>
  }
  reviewed: {
    approved: number
    approvedWithReservation: number
    rejected: number
    reviewedByAi: number
    notReviewed: number
    total: number
  }
  averageCompletionTime: {
    days: number
    percentageChange: number
    value: number // raw value for calculation
    unit: 'minutes' | 'hours' | 'days' // unit to display
  }
  overdueTasks: {
    count: number
    percentageChange: number
  }
  overdueContexts: {
    count: number // count of overdue contexts
    total: number // total contexts
    percentage: number // percentage of overdue contexts
  }
  storeCompletion: {
    allCompleted: number
    inProgress: number
    notStarted: number
    allApproved: number
    allRejected: number
    total: number
  }
  completionTimeDetails: Array<{
    taskId: string
    taskTitle: string
    completionTime: number // in days
    unit: 'minutes' | 'hours' | 'days'
    displayValue: number
    allocatedTime: number // deadline - from_ts in days
    allocatedUnit: 'minutes' | 'hours' | 'days'
    allocatedDisplayValue: number
    fromTs: string // from_ts timestamp string
    deadline: string // deadline timestamp string
  }>
  overdueTasksDetails: Array<{
    taskId: string
    taskTitle: string
    contextId: string
    overdueTime: number // in days (how late from deadline)
    unit: 'minutes' | 'hours' | 'days'
    displayValue: number
    contextStatus: 'completed' | 'partial' | 'not_started'
    allocatedTime: number // deadline - from_ts in days
    allocatedUnit: 'minutes' | 'hours' | 'days'
    allocatedDisplayValue: number
    fromTs: string // from_ts timestamp string
    deadline: string // deadline timestamp string
    completedTs: number // completion timestamp (0 if not completed)
  }>
  storeCompletedInTimeDetails: Array<{
    taskId: string
    taskTitle: string
    contextId: string
    completionTime: number // in days
    unit: 'minutes' | 'hours' | 'days'
    displayValue: number
    allocatedTime: number // deadline - from_ts in days
    allocatedUnit: 'minutes' | 'hours' | 'days'
    allocatedDisplayValue: number
    fromTs: string // from_ts timestamp string
    deadline: string // deadline timestamp string
  }>
  contextTableData: Array<{
    contextId: string
    contextName: string
    tasksDone: number // completed tasks count (all subtasks done/approved/rejected/skipped)
    tasksTotal: number // total tasks count
    subtasksDone: number // done + approved + rejected subtasks (NOT skipped)
    subtasksTotal: number // total subtasks with any result
    subtasksSkipped: number // skipped subtasks
    averageRank: number // average rating
    rejected: number // rejected count
    approved: number // approved count
    approvedWithReservation: number // approved-with-reservation count (status 5)
    reviewedByAi: number // AI-reviewed count (status -5)
    notReviewed: number // subtasks with status DONE from type 2 tasks (not yet reviewed)
    type2SubtasksTotal: number // total subtasks from type 2 tasks
    subtasksOnTime: number // subtasks completed before deadline
    subtasksLate: number // subtasks completed after deadline
    runTime: number // time from first to last result (in days)
    quality: number | null // average quality score for VM tasks (type 2), null if no VM tasks
    reworkCount: number | null // sum of all rework_count, null if no results at all
  }>
  // NEW: Pre-calculated chart data (OPTIONAL - for backward compatibility)
  charts?: {
    completionDonut: {
      data: Array<{ name: string; value: number; color: string; percentage: number; textColor?: string }>
      centerValue: number
      centerLabel: string
      total: number
      completionRate: number
    }
    storeCompletion: {
      data: Array<{ name: string; value: number; color: string; percentage: number }>
      total: number
    }
    skippedRanking: {
      data: Array<{ name: string; value: number; color: string; percentage: number }>
      maxValue: number
      totalSkipped: number
    }
    reviewedStatus: {
      data: Array<{ name: string; value: number; color: string; percentage: number }>
      total: number
    }
    ratingData: {
      averageRating: number
      totalRatings: number
      distribution: Array<{ stars: number; count: number; percentage: number; color: string }>
      maxDistribution: number
      unratedCount: number
      unratedPercentage: number
    }
  }
}

// Color palette for charts
export const CHART_COLORS = {
  done: '#CBE3FF',
  doneText: '#4191FF',
  inProgress: '#FFF2CC',
  inProgressText: '#eba245',
  todo: '#E2E2E2',
  todoText: '#6F6F6F',
  approved: '#CBEAC1',
  approvedText: '#2dad35',
  rejected: '#FCC',
  rejectedText: '#B91C1C',
  notReviewed: '#E2E2E2',
  notReviewedText: '#6F6F6F',
  reviewedByAi: '#EDE9FE',
  reviewedByAiText: '#7C3AED',
}

export const TASK_STATUS_CONFIG = {
  todo: {
    localeKey: 'task_status_todo',
    bg: '#F8FAFC',
    border: '#E2E8F0',
    text: '#475569',
    bar: CHART_COLORS.todoText,
    labelOnBar: '#ffffff',
  },
  inProgress: {
    localeKey: 'task_status_inprogress',
    bg: CHART_COLORS.inProgress,
    border: '#FDE68A',
    text: '#92400E',
    bar: CHART_COLORS.inProgressText,
    labelOnBar: '#ffffff',
  },
  done: {
    localeKey: 'task_status_done',
    bg: CHART_COLORS.done,
    border: '#BFDBFE',
    text: '#1D4ED8',
    bar: CHART_COLORS.doneText,
    labelOnBar: '#ffffff',
  },
  completed: {
    localeKey: 'task_status_completed',
    bg: CHART_COLORS.done,
    border: '#BFDBFE',
    text: '#1D4ED8',
    bar: CHART_COLORS.doneText,
    labelOnBar: '#ffffff',
  },
  approved: {
    localeKey: 'task_status_approved',
    bg: CHART_COLORS.approved,
    border: '#BBF7D0',
    text: '#047857',
    bar: CHART_COLORS.approvedText,
    labelOnBar: '#ffffff',
  },
  approvedWithReservation: {
    localeKey: 'task_status_approved_reservation',
    bg: '#CCFBF1',
    border: '#99F6E4',
    text: '#0F766E',
    bar: '#14B8A6',
    labelOnBar: '#ffffff',
  },
  rejected: {
    localeKey: 'task_status_rejected',
    bg: CHART_COLORS.rejected,
    border: '#FECACA',
    text: CHART_COLORS.rejectedText,
    bar: '#F87171',
    labelOnBar: '#ffffff',
  },
  skipped: {
    localeKey: 'task_status_skipped',
    bg: '#FFEDD5',
    border: '#FED7AA',
    text: '#C2410C',
    bar: '#F97316',
    labelOnBar: '#ffffff',
  },
  notDone: {
    localeKey: 'task_status_todo',
    bg: CHART_COLORS.todo,
    border: '#CBD5E1',
    text: '#334155',
    bar: '#B8C0CA',
    labelOnBar: '#334155',
  },
  notReviewed: {
    localeKey: 'task_status_not_reviewed',
    bg: CHART_COLORS.notReviewed,
    border: '#E2E8F0',
    text: CHART_COLORS.inProgressText,
    bar: CHART_COLORS.inProgressText,
    labelOnBar: '#ffffff',
  },
  reviewedByAi: {
    localeKey: 'task_status_reviewed_ai',
    bg: CHART_COLORS.reviewedByAi,
    border: '#DDD6FE',
    text: '#6D28D9',
    bar: CHART_COLORS.reviewedByAiText,
    labelOnBar: '#ffffff',
  },
} as const

/** @deprecated use TASK_STATUS_CONFIG */
export const ANALYTICS_STATUS_COLORS = TASK_STATUS_CONFIG
/**
 * Get context name from contextId using groups or retail tags
 */
const getContextNameHelper = (
  contextId: string,
  groupsObject: Record<string, IConfigGroup>,
  retailTags: ITag[]
): string => {
  // Check in groups first
  if (groupsObject?.[contextId]?.name) {
    return groupsObject[contextId].name
  }

  // Check in retail tags (users)
  const user = retailTags.find((tag) => tag.sk === contextId)
  if (user) {
    return user.name || ''
  }

  return contextId // Fallback to ID if name not found
}

/**
 * Extracts UIDs from groups - optimized with early exit
 */
const extractUidsFromGroups = (groupIds: string[], groupsObject: Record<string, IConfigGroup>): string[] => {
  const uidsSet = new Set<string>()
  for (let i = 0; i < groupIds.length; i++) {
    const group = groupsObject[groupIds[i]]
    if (group?.uids) {
      for (let j = 0; j < group.uids.length; j++) {
        uidsSet.add(group.uids[j])
      }
    }
  }
  return Array.from(uidsSet)
}

/**
 * Resolves contexts for a task - returns array of context IDs
 * Optimized: single pass, reuses sets
 */
const resolveTaskContexts = (
  task: TaskFromInsights,
  groupsObject: Record<string, IConfigGroup>,
  retailTags: ITag[]
): string[] => {
  const { users, groups, tags, is_individual_results } = task

  // Build tag lookup map once (cached at higher level ideally)
  const tagGroups: string[] = []
  const tagUsers: string[] = []

  for (let i = 0; i < tags.length; i++) {
    const tag = retailTags.find((t) => t.sk === tags[i])
    if (tag) {
      if (tag.groups) tagGroups.push(...tag.groups)
      if (tag.users) tagUsers.push(...tag.users)
    }
  }

  if (is_individual_results) {
    // Merge all groups, then extract all users
    const allGroupsSet = new Set([...groups, ...tagGroups])
    const usersFromGroups = extractUidsFromGroups(Array.from(allGroupsSet), groupsObject)
    const allUsersSet = new Set([...users, ...tagUsers, ...usersFromGroups])
    return Array.from(allUsersSet)
  } else {
    // Groups stay as groups, users stay as users
    const allGroupsSet = new Set([...groups, ...tagGroups])
    const allUsersSet = new Set([...users, ...tagUsers])
    return [...Array.from(allGroupsSet), ...Array.from(allUsersSet)]
  }
}

/**
 * The server counts the original submission inside `rework_count`, so a result that was never sent
 * back for rework still arrives as `1`. Analytics reports actual reworks, hence the `- 1`.
 *
 * Applied per RESULT, not to a sum — subtracting one from an already-aggregated total would swallow a
 * single rework across N results. Normalized here, in the one place analytics reads results, so the
 * ranking / subtasks / categories tables and the quality penalty can never drift apart.
 */
const normalizeReworkCount = (reworkCount?: number | null): number | undefined => {
  if (reworkCount === null || reworkCount === undefined) return undefined
  return reworkCount > 0 ? reworkCount - 1 : reworkCount
}

/**
 * Build a lookup map for results: contextId -> subtaskId -> result data
 * This allows O(1) lookups instead of O(n) array searches
 */
const buildResultsLookup = (
  results: TaskInsightsResult[]
): Map<
  string,
  Map<
    string,
    { status: number; rating?: number; created_at_ts?: number; rework_count?: number; score_deduction?: number }
  >
> => {
  const lookup = new Map<
    string,
    Map<
      string,
      { status: number; rating?: number; created_at_ts?: number; rework_count?: number; score_deduction?: number }
    >
  >()

  for (let i = 0; i < results.length; i++) {
    const r = results[i]
    if (!lookup.has(r.context_id)) {
      lookup.set(r.context_id, new Map())
    }
    lookup.get(r.context_id)!.set(r.st_id, {
      status: r.status,
      rating: r.rating,
      created_at_ts: r.created_at_ts,
      rework_count: normalizeReworkCount(r.rework_count),
      // score_deduction is returned at the result top level (nullable); reservations nesting
      // is a legacy shape. `?? undefined` keeps a real 0 but drops null so quality math works.
      score_deduction: r.reservations?.score_deduction ?? r.score_deduction ?? undefined,
    })
  }

  return lookup
}

// Status check helpers
const isCompletedStatus = (status: number): boolean => {
  return (
    status === RESULT_STATUSES.DONE ||
    status === RESULT_STATUSES.SKIPPED ||
    status === RESULT_STATUSES.APPROVED ||
    status === RESULT_STATUSES.APPROVED_WITH_RESERVATION ||
    status === RESULT_STATUSES.DISAPPROVED ||
    status === RESULT_STATUSES.REVIEWED_BY_AI
  )
}

/**
 * Statuses that carry a quality verdict, i.e. the ones `quality` is averaged over.
 *
 * Quality is the AUDITOR's score, not a measure of progress — so a result only counts once it has
 * been judged. Everything else (no result, `INPROGRESS`, `DONE` awaiting review, `SKIPPED` =
 * not applicable) is excluded from BOTH the numerator and the denominator and surfaces as a dash,
 * never as a 0. Counting unjudged work as 0 used to drag a perfectly reviewed subtask down to 6.9%
 * across 29 mostly-untouched contexts, which read as "terrible quality" instead of "not started" —
 * the progress bar next to the number already says that. `REVIEWED_BY_AI` counts: it is a finished
 * audit, just performed by the AI (consistent with `utils/taskStatusModel`, where it folds into
 * approved).
 */
const isAuditedStatus = (status: number): boolean => {
  return (
    status === RESULT_STATUSES.APPROVED ||
    status === RESULT_STATUSES.APPROVED_WITH_RESERVATION ||
    status === RESULT_STATUSES.DISAPPROVED ||
    status === RESULT_STATUSES.REVIEWED_BY_AI
  )
}

/**
 * Quality for one audited result: full marks minus what was deducted.
 * Rejection is a total loss; otherwise deduct the auditor's `score_deduction`, 10 per rework and a
 * flat late penalty. A result with no timestamp simply can't be late — it is still scored.
 */
const calculateResultQuality = (
  status: number,
  scoreDeduction: number | undefined,
  reworkCount: number | undefined,
  createdAtTs: number | undefined,
  deadlineTimestamp: number
): number => {
  if (status === RESULT_STATUSES.DISAPPROVED) return 0

  const completedAtMs = createdAtTs ? createdAtTs * 1000 : 0
  const isLate = deadlineTimestamp > 0 && completedAtMs > deadlineTimestamp
  const timePenalty = isLate ? LATE_QUALITY_PENALTY : 0

  return Math.max(
    0,
    MAX_QUALITY_SCORE - (scoreDeduction ?? 0) - (reworkCount ?? 0) * REWORK_QUALITY_PENALTY - timePenalty
  )
}

/**
 * Parse timestamp string in format YYYYMMDDHHmmss to Unix timestamp (ms)
 */
const parseTimestampString = (ts: string): number => {
  if (!ts || ts.length < 12) return 0

  const year = parseInt(ts.substring(0, 4), 10)
  const month = parseInt(ts.substring(4, 6), 10) - 1 // months are 0-indexed
  const day = parseInt(ts.substring(6, 8), 10)
  const hour = parseInt(ts.substring(8, 10), 10)
  const minute = parseInt(ts.substring(10, 12), 10)
  const second = ts.length >= 14 ? parseInt(ts.substring(12, 14), 10) : 0

  return new Date(year, month, day, hour, minute, second).getTime()
}

/**
 * Filter insights by task IDs
 * Returns a subset of insights containing only tasks with specified IDs
 */
export const filterInsightsByTaskIds = (insights: TasksInsights, taskIds: string[]): TasksInsights => {
  const taskIdsSet = new Set(taskIds)
  const filtered: TasksInsights = {}

  for (const tid in insights) {
    if (taskIdsSet.has(tid)) {
      filtered[tid] = insights[tid]
    }
  }

  return filtered
}

/**
 * Audience dimension a user can filter the analytics data by.
 */
export type AudienceDimension = 'groups' | 'users' | 'tags' | 'auditors'

/**
 * Sentinel entity id for the `tags` dimension: contexts covered by NO tag at all. Mirrors the
 * task-preview grouping, which always appends an "untagged" section — without it those contexts are
 * unreachable through the tags filter. It is not a tag `sk`, so `filterInsightsByAudience` matches it
 * by negation instead of by tag membership.
 */
export const UNTAGGED_AUDIENCE_ID = '__untagged__'

// Every group / user id covered by at least one tag — the complement is "untagged".
const collectTaggedContextIds = (retailTags: ITag[]) => {
  const taggedGroups = new Set<string>()
  const taggedUsers = new Set<string>()
  ;(retailTags || []).forEach((tag) => {
    tag.groups?.forEach((id) => taggedGroups.add(id))
    tag.users?.forEach((id) => taggedUsers.add(id))
  })
  return { taggedGroups, taggedUsers }
}

export interface IAudienceEntityIds {
  groups: string[]
  users: string[]
  tags: string[]
  auditors: string[]
}

/**
 * Per dimension, the ids that can actually match something in `insights`.
 *
 * The audience filter must be offered against the tasks currently in scope, not against the whole
 * org config: analytics is usually opened for a single task (`?taskIds=`), and listing every org
 * group / user / tag / auditor there produces options that are guaranteed to return no data — a
 * selectable auditor who never audits any analyzed task looks like a bug to the user. This mirrors
 * the task-preview grouping (`useContextGrouping`), which is scoped to the previewed task by
 * construction.
 *
 * Ids come from each task's RESOLVED contexts (so a task carrying only `groups` +
 * `is_individual_results` still yields its user contexts), plus its own `auditors`. A tag is offered
 * only when it covers one of those contexts — the same matching rule `filterInsightsByAudience`
 * applies — plus `UNTAGGED_AUDIENCE_ID` when some context is covered by no tag at all.
 */
export const collectAudienceEntityIds = (
  insights: TasksInsights,
  groupsObject: Record<string, IConfigGroup>,
  retailTags: ITag[]
): IAudienceEntityIds => {
  const groups = new Set<string>()
  const users = new Set<string>()
  const auditors = new Set<string>()

  Object.values(insights || {}).forEach((task) => {
    task.auditors?.forEach((uid) => uid && auditors.add(uid))
    resolveTaskContexts(task, groupsObject, retailTags).forEach((contextId) => {
      if (groupsObject?.[contextId]) groups.add(contextId)
      else users.add(contextId)
    })
  })

  const tags = (retailTags || [])
    .filter((tag) => tag.groups?.some((id) => groups.has(id)) || tag.users?.some((id) => users.has(id)))
    .map((tag) => tag.sk)

  const { taggedGroups, taggedUsers } = collectTaggedContextIds(retailTags)
  const hasUntagged =
    Array.from(groups).some((id) => !taggedGroups.has(id)) || Array.from(users).some((id) => !taggedUsers.has(id))
  if (hasUntagged) tags.push(UNTAGGED_AUDIENCE_ID)

  return { groups: Array.from(groups), users: Array.from(users), tags, auditors: Array.from(auditors) }
}

export interface AudienceFilterMaps {
  groupsObject: Record<string, IConfigGroup>
  retailTags: ITag[]
  mappedGroupIdsByUsers?: Record<string, string[]>
  mappedGroupsByUsersByFeature?: { [feature: string]: Record<string, string[]> }
  // Current session user — used to clip an auditor's org-tree-derived reach (see
  // getOwnVisibilityGroupIds below) to what the viewer themselves can see.
  viewerUid?: string | null
}

// Recursively collect a group and every descendant group id below it in the org tree.
// Shared with useContextGrouping so auditor-reach resolution can't drift between the
// task-preview grouping and this audience filter.
const collectGroupSubtreeIds = (groupId: string, groupsObject: Record<string, { child?: string[] }>): string[] => {
  const result = [groupId]
  const children = groupsObject?.[groupId]?.child
  children?.forEach((childId) => result.push(...collectGroupSubtreeIds(childId, groupsObject)))
  return result
}

// A user's own visibility area: their own group(s) plus everything below them in the org tree.
export const getOwnVisibilityGroupIds = (
  uid: string,
  mappedGroupIdsByUsers: Record<string, string[]> | undefined,
  groupsObject: Record<string, { child?: string[] }>
): Set<string> => {
  const ownGroupIds = mappedGroupIdsByUsers?.[uid] ?? []
  const result = new Set<string>()
  ownGroupIds.forEach((groupId) => collectGroupSubtreeIds(groupId, groupsObject).forEach((id) => result.add(id)))
  return result
}

/**
 * Filter insights by an audience selection (groups / users / tags / auditors).
 *
 * Works at the CONTEXT level: for every task it keeps only the contexts (and their results)
 * that match the selection, then drops tasks left with no matching context. The kept contexts
 * are written back onto the task's audience arrays (resolved form: groups + users, tags cleared,
 * is_individual_results = false) so `resolveTaskContexts` reproduces exactly the allowed set and
 * every analytics calculation (aggregated / subtask breakdown / categories) recomputes against it.
 *
 * Matching per dimension mirrors the dashboard grouping logic (`useContextGrouping`):
 * - groups:   context's group ids intersect the selected groups
 * - users:    a user context is selected directly, or a group context contains a selected user
 * - tags:     context belongs to a selected tag's groups/users
 * - auditors: scoped to each task's OWN auditors that are also selected. An auditor explicitly
 *             authorized via `mappedGroupsByUsersByFeature.VM` is trusted as-is (backend-vetted).
 *             An auditor present on the task but missing from that map ("manually assigned") has
 *             no explicit grant, so their reach is derived from the org tree — their own group(s)
 *             plus everything below — clipped to the current viewer's own org-tree reach so the
 *             viewer never sees contexts outside their own permitted subtree through another
 *             auditor's broader reach. A resulting empty set is a legitimate zero, not a bug.
 */
export const filterInsightsByAudience = (
  insights: TasksInsights,
  dimension: AudienceDimension,
  ids: string[],
  maps: AudienceFilterMaps
): TasksInsights => {
  if (!ids || ids.length === 0) return insights

  const { groupsObject, retailTags, mappedGroupIdsByUsers, mappedGroupsByUsersByFeature, viewerUid } = maps
  const idSet = new Set(ids)

  // Pre-expand the selection into the group/user sets we match contexts against (task-independent).
  const tagGroupSet = new Set<string>()
  const tagUserSet = new Set<string>()
  // Per selected-auditor reach (VM-authorized groups, or org-tree fallback clipped to the viewer).
  const auditorGroupSets = new Map<string, Set<string>>()

  // `tags` may also carry the UNTAGGED sentinel — matched by absence from every tag, not membership.
  const isUntaggedSelected = dimension === 'tags' && idSet.has(UNTAGGED_AUDIENCE_ID)
  const { taggedGroups, taggedUsers } = isUntaggedSelected
    ? collectTaggedContextIds(retailTags)
    : { taggedGroups: new Set<string>(), taggedUsers: new Set<string>() }

  if (dimension === 'tags') {
    for (let i = 0; i < retailTags.length; i++) {
      const tag = retailTags[i]
      if (!idSet.has(tag.sk)) continue
      tag.groups?.forEach((g) => tagGroupSet.add(g))
      tag.users?.forEach((u) => tagUserSet.add(u))
    }
  } else if (dimension === 'auditors') {
    const vmMap = mappedGroupsByUsersByFeature?.VM ?? {}
    let viewerVisibilityGroupIds: Set<string> | null = null

    idSet.forEach((auditorUid) => {
      const vmGroupIds = vmMap[auditorUid] ?? []
      if (vmGroupIds.length > 0) {
        auditorGroupSets.set(auditorUid, new Set(vmGroupIds))
        return
      }

      if (!viewerVisibilityGroupIds) {
        viewerVisibilityGroupIds = viewerUid
          ? getOwnVisibilityGroupIds(viewerUid, mappedGroupIdsByUsers, groupsObject)
          : new Set<string>()
      }
      const auditorOwnGroupIds = getOwnVisibilityGroupIds(auditorUid, mappedGroupIdsByUsers, groupsObject)
      auditorGroupSets.set(
        auditorUid,
        new Set(Array.from(auditorOwnGroupIds).filter((id) => viewerVisibilityGroupIds!.has(id)))
      )
    })
  }

  const contextGroupIdsOf = (contextId: string): string[] =>
    groupsObject[contextId] ? [contextId] : mappedGroupIdsByUsers?.[contextId] ?? []

  const matchContext = (contextId: string, task: TaskFromInsights): boolean => {
    // Group ids and user ids live in disjoint id spaces, so a groupsObject hit === group context.
    const isGroupContext = !!groupsObject[contextId]
    switch (dimension) {
      case 'groups':
        return contextGroupIdsOf(contextId).some((g) => idSet.has(g))
      case 'users':
        // Individual user contexts only (a group context id can't be in the selected-user set).
        return idSet.has(contextId)
      case 'tags': {
        // Parity with useContextGrouping 'tags' mode: group context in tag.groups, user in tag.users,
        // and the untagged sentinel catching whatever no tag covers.
        if (isGroupContext) return tagGroupSet.has(contextId) || (isUntaggedSelected && !taggedGroups.has(contextId))
        return tagUserSet.has(contextId) || (isUntaggedSelected && !taggedUsers.has(contextId))
      }
      case 'auditors': {
        // Mirror useContextGrouping 'auditors' mode, scoped to THIS task's auditors: only
        // selected auditors actually assigned to the task count. See getOwnVisibilityGroupIds
        // and auditorGroupSets above for how each auditor's reach was resolved.
        const taskAuditors = (task.auditors || []).filter((a) => idSet.has(a))
        if (taskAuditors.length === 0) return false
        const ctxGroups = contextGroupIdsOf(contextId)
        return taskAuditors.some((a) => {
          const set = auditorGroupSets.get(a)
          return !!set && ctxGroups.some((g) => set.has(g))
        })
      }
      default:
        return true
    }
  }

  const filtered: TasksInsights = {}

  for (const tid in insights) {
    const task = insights[tid]
    const contexts = resolveTaskContexts(task, groupsObject, retailTags)
    const allowed = contexts.filter((contextId) => matchContext(contextId, task))
    if (allowed.length === 0) continue

    const allowedSet = new Set(allowed)
    filtered[tid] = {
      ...task,
      groups: allowed.filter((c) => !!groupsObject[c]),
      users: allowed.filter((c) => !groupsObject[c]),
      tags: [],
      is_individual_results: false,
      results: (task.results || []).filter((r) => allowedSet.has(r.context_id)),
    }
  }

  return filtered
}

/**
 * SINGLE-PASS ANALYTICS CALCULATION
 * Calculates ALL metrics in one iteration through data
 * Optimized for hundreds of tasks with thousands of contexts each
 */
export const calculateAggregatedAnalytics = (
  insights: TasksInsights,
  groupsObject: Record<string, IConfigGroup>,
  retailTags: ITag[],
  // Auditor tables need the same metrics, but never open the dashboard detail modals.
  { includeDetails = true }: { includeDetails?: boolean } = {}
): TaskAnalytics => {
  // Initialize accumulators
  const result: TaskAnalytics = {
    summary: {
      todo: 0,
      inProgress: 0,
      done: 0,
      approved: 0,
      rejected: 0,
      total: 0,
    },
    completion: { done: 0, inProgress: 0, todo: 0, skipped: 0, approved: 0, disapproved: 0, blocked: 0, total: 0 },
    skippedRanking: [],
    completedInTime: { total: 0, completedInTime: 0, completedLate: 0, percentage: 0 },
    storeCompletedInTime: { total: 0, completedInTime: 0, completedLate: 0, percentage: 0 },
    rating: {
      averageAll: 0,
      averageRatedOnly: 0,
      totalRatings: 0,
      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    },
    reviewed: { approved: 0, approvedWithReservation: 0, rejected: 0, reviewedByAi: 0, notReviewed: 0, total: 0 },
    averageCompletionTime: { days: 0, percentageChange: 0, value: 0, unit: 'days' },
    overdueTasks: { count: 0, percentageChange: 0 },
    overdueContexts: { count: 0, total: 0, percentage: 0 },
    storeCompletion: { allCompleted: 0, inProgress: 0, notStarted: 0, allApproved: 0, allRejected: 0, total: 0 },
    completionTimeDetails: [],
    overdueTasksDetails: [],
    storeCompletedInTimeDetails: [],
    contextTableData: [],
  }

  const taskIds = Object.keys(insights)
  if (taskIds.length === 0) return result

  // Accumulators for aggregation
  let totalRatingSum = 0
  let totalRatingCount = 0
  let ratedRatingSum = 0
  let ratedRatingCount = 0

  // Skipped tracking: subtaskId -> { title, count, contexts }
  const skippedMap = new Map<
    string,
    {
      title: string
      count: number
      contexts: Array<{ contextId: string; taskId: string; taskTitle: string; skippedAt: number; deadline: string }>
    }
  >()

  // Completion time tracking
  let completionTimeSum = 0
  let completionTimeCount = 0

  // Current timestamp for overdue calculation
  const currentTimestamp = Date.now()

  // Track unique overdue tasks and contexts
  const overdueTasksSet = new Set<string>()
  const overdueContextsSet = new Set<string>()

  // Track unique tasks by status (for summary)
  const doneTasksSet = new Set<string>()
  const todoTasksSet = new Set<string>()
  const inProgressTasksSet = new Set<string>()
  const blockedTasksSet = new Set<string>()

  // Track unique contexts (stores) for storeCompletion
  const allContextsSet = new Set<string>()
  const notStartedContextsSet = new Set<string>()
  const inProgressContextsSet = new Set<string>()
  const allCompletedContextsSet = new Set<string>()
  const allApprovedContextsSet = new Set<string>()
  const allRejectedContextsSet = new Set<string>()

  // Track context-level data for table
  const contextDataMap = new Map<
    string,
    {
      tasksDone: number
      tasksTotal: number
      subtasksDone: number // done + approved + rejected (NOT skipped)
      subtasksTotal: number // ALL subtasks assigned to this context
      subtasksSkipped: number
      ratingSum: number
      ratingCount: number
      rejected: number
      approved: number
      approvedWithReservation: number // status 5 count
      reviewedByAi: number // status -5 count
      notReviewed: number // subtasks with status DONE from type 2 tasks
      type2SubtasksTotal: number // total subtasks from type 2 tasks
      subtasksOnTime: number // subtasks completed before deadline
      subtasksLate: number // subtasks completed after deadline
      firstResultTs: number // earliest result timestamp
      lastResultTs: number // latest result timestamp
      // Quality metrics (VM tasks type=2 only, audited results only — see isAuditedStatus)
      qualitySum: number // sum of quality scores over audited VM subtasks
      qualityCount: number // count of audited VM subtasks; 0 → quality is null (dash)
      // Rework count
      reworkCountSum: number // sum of all rework_count for all results
      hasAnyResult: boolean // true if context has at least one result
    }
  >()

  // Process each task
  for (let t = 0; t < taskIds.length; t++) {
    const tid = taskIds[t]
    const task = insights[tid]
    const subtaskIds = Object.keys(task.sub_tasks)
    const subtaskCount = subtaskIds.length

    if (subtaskCount === 0) continue

    // Resolve contexts for this task
    const contexts = resolveTaskContexts(task, groupsObject, retailTags)
    const contextCount = contexts.length

    if (contextCount === 0) continue

    // Build lookup map for O(1) result access
    const resultsLookup = buildResultsLookup(task.results || [])

    // Parse deadline and from_ts once per task (cached for all contexts)
    const deadlineTimestamp = parseTimestampString(task.deadline)
    const fromTimestamp = parseTimestampString(task.from_ts)

    // Track task-level completion metrics
    let taskEarliestCompletionTimestamp = 0
    let taskEarliestResultTimestamp = 0 // First result timestamp (start of actual work)
    let taskIsCompleted = false
    let taskCompletedInTime = false
    let taskHasCompletionTime = false

    // Track task-level status (check across all contexts)
    let taskHasAnyResult = false
    let taskIsFullyCompleted = true
    let taskIsBlocked = false

    // Process each context in a SINGLE PASS
    for (let c = 0; c < contextCount; c++) {
      const contextId = contexts[c]
      const contextResults = resultsLookup.get(contextId)

      // Initialize context data in map if not exists
      if (!contextDataMap.has(contextId)) {
        contextDataMap.set(contextId, {
          tasksDone: 0,
          tasksTotal: 0,
          subtasksDone: 0, // done + approved + rejected (NOT skipped)
          subtasksTotal: 0, // ALL subtasks assigned to this context
          subtasksSkipped: 0,
          ratingSum: 0,
          ratingCount: 0,
          rejected: 0,
          approved: 0,
          approvedWithReservation: 0,
          reviewedByAi: 0,
          notReviewed: 0, // subtasks with status DONE from type 2 tasks
          type2SubtasksTotal: 0, // total subtasks from type 2 tasks
          subtasksOnTime: 0,
          subtasksLate: 0,
          firstResultTs: 0,
          lastResultTs: 0,
          // Quality metrics
          qualitySum: 0,
          qualityCount: 0,
          // Rework count
          reworkCountSum: 0,
          hasAnyResult: false,
        })
      }
      const contextData = contextDataMap.get(contextId)!

      // Increment total tasks for this context
      contextData.tasksTotal++

      // Track per-context metrics
      let contextHasAnyResult = false
      let contextAllSubtasksCompleted = true
      let latestCompletionTimestamp = 0
      let contextCompletedInTime = true // Track if all results are completed before deadline
      let contextCompletionTimeSum = 0 // Sum of completion times for average
      let contextCompletedResultsCount = 0 // Count of completed results

      // Process each subtask for this context
      for (let s = 0; s < subtaskCount; s++) {
        const subtaskId = subtaskIds[s]
        const resultData = contextResults?.get(subtaskId)

        // Count ALL subtasks for this context (regardless of result)
        contextData.subtasksTotal++

        // Track type 2 task subtasks (require approval)
        if (task.type === VM_TASK_TYPE) {
          contextData.type2SubtasksTotal++
        }

        // --- COMPLETION METRICS (subtask level) ---
        if (!resultData) {
          // No result = TODO. Nothing to score — deliberately absent from the quality average.
          result.completion.todo++
          contextAllSubtasksCompleted = false
        } else {
          contextHasAnyResult = true
          contextData.hasAnyResult = true // Mark that this context has at least one result
          const status = resultData.status

          // Track rework count
          if (resultData.rework_count !== undefined && resultData.rework_count !== null) {
            contextData.reworkCountSum += resultData.rework_count
          }

          // Track first and last result timestamps for run time calculation
          if (resultData.created_at_ts) {
            const resultTs = resultData.created_at_ts * 1000
            if (contextData.firstResultTs === 0 || resultTs < contextData.firstResultTs) {
              contextData.firstResultTs = resultTs
            }
            if (resultTs > contextData.lastResultTs) {
              contextData.lastResultTs = resultTs
            }

            // Track earliest result timestamp across all contexts for task-level completion time
            // This gives us the actual start of work, not the task from_ts
            if (taskEarliestResultTimestamp === 0 || resultTs < taskEarliestResultTimestamp) {
              taskEarliestResultTimestamp = resultTs
            }
          }

          // Count by status
          switch (status) {
            case RESULT_STATUSES.DONE:
              result.completion.done++
              contextData.subtasksDone++ // done counts as "done"
              // Track not reviewed for type 2 tasks (status DONE means not yet reviewed)
              if (task.type === VM_TASK_TYPE) {
                contextData.notReviewed++
              }
              break
            case RESULT_STATUSES.INPROGRESS:
              result.completion.inProgress++
              contextAllSubtasksCompleted = false
              break
            case RESULT_STATUSES.SKIPPED:
              result.completion.skipped++
              contextData.subtasksSkipped++
              // Track for skipped ranking with context info
              const existingSkipped = skippedMap.get(subtaskId)
              const skippedAtTs = resultData.created_at_ts ? resultData.created_at_ts * 1000 : 0
              if (existingSkipped) {
                existingSkipped.count++
                if (includeDetails) {
                  existingSkipped.contexts.push({
                    contextId,
                    taskId: tid,
                    taskTitle: task.title || 'Untitled Task',
                    skippedAt: skippedAtTs,
                    deadline: task.deadline || '',
                  })
                }
              } else {
                skippedMap.set(subtaskId, {
                  title: getInsightsSubtaskTitle(task.sub_tasks[subtaskId], subtaskId),
                  count: 1,
                  contexts: includeDetails
                    ? [
                        {
                          contextId,
                          taskId: tid,
                          taskTitle: task.title || 'Untitled Task',
                          skippedAt: skippedAtTs,
                          deadline: task.deadline || '',
                        },
                      ]
                    : [],
                })
              }
              break
            case RESULT_STATUSES.APPROVED:
            case RESULT_STATUSES.APPROVED_WITH_RESERVATION:
              result.completion.approved++
              contextData.subtasksDone++ // approved counts as "done"
              // Keep approved / approved-with-reservation in separate columns for the ranking table
              if (status === RESULT_STATUSES.APPROVED_WITH_RESERVATION) {
                contextData.approvedWithReservation++
              } else {
                contextData.approved++
              }
              // Only count reviewed stats for type 2 tasks (TRAINING_TASK)
              if (task.type === VM_TASK_TYPE) {
                if (status === RESULT_STATUSES.APPROVED_WITH_RESERVATION) {
                  result.reviewed.approvedWithReservation++
                } else {
                  result.reviewed.approved++
                }
                result.reviewed.total++
              }
              break
            case RESULT_STATUSES.DISAPPROVED:
              result.completion.disapproved++
              contextData.subtasksDone++ // rejected also counts as "done"
              contextData.rejected++
              // Only count reviewed stats for type 2 tasks (TRAINING_TASK)
              if (task.type === VM_TASK_TYPE) {
                result.reviewed.rejected++
                result.reviewed.total++
              }
              break
            case RESULT_STATUSES.REVIEWED_BY_AI:
              result.completion.done++
              contextData.subtasksDone++
              contextData.reviewedByAi++
              // AI-reviewed is completed but not yet human-reviewed
              if (task.type === VM_TASK_TYPE) {
                contextData.notReviewed++
                result.reviewed.reviewedByAi++
                result.reviewed.total++
              }
              break
            default:
              result.completion.inProgress++
              contextAllSubtasksCompleted = false
          }

          // Check if completed status for summary
          if (!isCompletedStatus(status)) {
            contextAllSubtasksCompleted = false
          }

          // Track latest completion timestamp for this context
          // Note: created_at_ts is in seconds (Unix timestamp), convert to milliseconds
          if (isCompletedStatus(status) && resultData.created_at_ts) {
            const completedAtMs = resultData.created_at_ts * 1000
            latestCompletionTimestamp = Math.max(latestCompletionTimestamp, completedAtMs)

            // Track on time / late at subtask level
            if (deadlineTimestamp > 0) {
              if (completedAtMs <= deadlineTimestamp) {
                contextData.subtasksOnTime++
              } else {
                contextData.subtasksLate++
              }
            }

            // Check if completed after deadline (for context-level tracking)
            if (deadlineTimestamp > 0 && completedAtMs > deadlineTimestamp) {
              contextCompletedInTime = false
            }

            // Calculate completion time for this subtask result
            // Time = when result was created - task start time (from_ts)
            if (fromTimestamp > 0) {
              const resultCompletionDays = (completedAtMs - fromTimestamp) / (1000 * 60 * 60 * 24)
              contextCompletionTimeSum += resultCompletionDays
              contextCompletedResultsCount++

              // Add to global average (for all completed subtasks across all tasks)
              completionTimeSum += resultCompletionDays
              completionTimeCount++
            }
          }

          // --- QUALITY CALCULATION (VM tasks type=2, audited results only) ---
          // Deliberately outside the `isCompletedStatus && created_at_ts` block above: quality is
          // scored per audit verdict, and a missing timestamp only means "can't be late" — it must
          // not drop the result out of the average entirely.
          if (task.type === VM_TASK_TYPE && isAuditedStatus(status)) {
            contextData.qualitySum += calculateResultQuality(
              status,
              resultData.score_deduction,
              resultData.rework_count,
              resultData.created_at_ts,
              deadlineTimestamp
            )
            contextData.qualityCount++
          }

          // --- RATING METRICS ---
          const rating = resultData.rating
          if (rating !== null && rating !== undefined) {
            if (rating >= 1 && rating <= 5) {
              ratedRatingSum += rating
              ratedRatingCount++
              result.rating.ratingDistribution[rating]++
              contextData.ratingSum += rating
              contextData.ratingCount++
            }
            totalRatingSum += rating || 0
          }
          totalRatingCount++

          // --- REVIEWED (for status === DONE which means not yet reviewed) ---
          // Only count reviewed stats for type 2 tasks (TRAINING_TASK)
          if (status === RESULT_STATUSES.DONE && task.type === VM_TASK_TYPE) {
            result.reviewed.notReviewed++
            result.reviewed.total++
          }
        }

        result.completion.total++
      }

      // --- SUMMARY METRICS (context level) ---
      result.summary.total++

      // --- STORE COMPLETED IN TIME METRICS (ALL contexts) ---
      // Count ALL contexts (including those that didn't complete)
      result.storeCompletedInTime.total++

      // --- STORE COMPLETION METRICS ---
      // Track unique contexts
      allContextsSet.add(contextId)

      if (!contextHasAnyResult) {
        // No results at all = TODO
        result.summary.todo++ // Count task-context pairs for summary
        notStartedContextsSet.add(contextId)
        taskIsFullyCompleted = false
        // Don't set taskHasAnyResult - remains false if all contexts have no results
      } else {
        taskHasAnyResult = true

        // Check if any subtask is rejected (regardless of completion status)
        const hasRejected = Array.from(contextResults?.values() || []).some(
          (r) => r.status === RESULT_STATUSES.DISAPPROVED
        )

        if (hasRejected) {
          // At least one subtask rejected = REJECTED (regardless of completion status)
          result.summary.rejected++
          allRejectedContextsSet.add(contextId)
          taskIsBlocked = true
          taskIsFullyCompleted = false
        } else if (contextAllSubtasksCompleted) {
          // All subtasks completed and none rejected. The approved/done split follows the
          // shared 5-status rule: every subtask signed off (approved / approved-with-reservation
          // / AI-reviewed, skipped being neutral) → approved; any plain DONE in the mix → done.
          const contextStatusCounts = countSubtaskStatuses(
            Array.from(contextResults?.values() || []).map((r) => r.status),
            subtaskCount
          )

          if (deriveTaskStatus(contextStatusCounts) === 'approved') {
            result.summary.approved++
            allApprovedContextsSet.add(contextId)
          } else {
            result.summary.done++ // All subtasks completed but not all signed off
            allCompletedContextsSet.add(contextId)
          }
        } else {
          // Some progress but not complete and not rejected = IN_PROGRESS
          result.summary.inProgress++
          inProgressContextsSet.add(contextId)
          taskIsFullyCompleted = false
        }

        // --- TRACK COMPLETION TIME FOR ALL COMPLETED CONTEXTS (including rejected) ---
        // This runs for REJECTED, APPROVED, and DONE contexts if all subtasks are completed
        if (contextAllSubtasksCompleted) {
          // Update context table data - task done
          contextData.tasksDone++

          // --- TRACK TASK-LEVEL COMPLETION TIME ---
          if (latestCompletionTimestamp > 0 && fromTimestamp > 0) {
            // Track for task-level metric (use earliest completion for the task)
            if (taskEarliestCompletionTimestamp === 0 || latestCompletionTimestamp < taskEarliestCompletionTimestamp) {
              taskEarliestCompletionTimestamp = latestCompletionTimestamp
              taskIsCompleted = true
              taskCompletedInTime = latestCompletionTimestamp <= deadlineTimestamp
              taskHasCompletionTime = true
            }
          }

          // --- TRACK STORE-LEVEL COMPLETED IN TIME ---
          // Only count completed contexts (all subtasks done)
          if (contextCompletedInTime) {
            result.storeCompletedInTime.completedInTime++
          } else {
            result.storeCompletedInTime.completedLate++
          }

          // Add to details for modal - ONLY contexts that completed IN TIME
          if (includeDetails && contextCompletedInTime && contextCompletedResultsCount > 0 && fromTimestamp > 0) {
            const avgCompletionDays = contextCompletionTimeSum / contextCompletedResultsCount
            if (avgCompletionDays > 0) {
              let unit: 'minutes' | 'hours' | 'days' = 'days'
              let displayValue = avgCompletionDays

              if (avgCompletionDays < 1 / 24) {
                displayValue = avgCompletionDays * 24 * 60
                unit = 'minutes'
              } else if (avgCompletionDays < 1) {
                displayValue = avgCompletionDays * 24
                unit = 'hours'
              }

              // Calculate allocated time (deadline - from_ts)
              const allocatedDays =
                deadlineTimestamp > 0 && fromTimestamp > 0
                  ? (deadlineTimestamp - fromTimestamp) / (1000 * 60 * 60 * 24)
                  : 0
              let allocatedUnit: 'minutes' | 'hours' | 'days' = 'days'
              let allocatedDisplayValue = allocatedDays

              if (allocatedDays < 1 / 24) {
                allocatedDisplayValue = allocatedDays * 24 * 60
                allocatedUnit = 'minutes'
              } else if (allocatedDays < 1) {
                allocatedDisplayValue = allocatedDays * 24
                allocatedUnit = 'hours'
              }

              result.storeCompletedInTimeDetails.push({
                taskId: tid,
                taskTitle: task.title || 'Untitled Task',
                contextId,
                completionTime: avgCompletionDays,
                unit,
                displayValue: Number(displayValue.toFixed(1)),
                allocatedTime: allocatedDays,
                allocatedUnit,
                allocatedDisplayValue: Number(allocatedDisplayValue.toFixed(1)),
                fromTs: task.from_ts || '',
                deadline: task.deadline || '',
              })
            }
          }
        }
      }

      // --- OVERDUE TASKS ---
      // Overdue if: deadline passed AND (not completed OR completed late)
      const isOverdue =
        deadlineTimestamp > 0 &&
        currentTimestamp > deadlineTimestamp &&
        (!contextAllSubtasksCompleted || !contextCompletedInTime)
      if (isOverdue) {
        overdueTasksSet.add(tid) // Add to set for unique task count
        overdueContextsSet.add(contextId) // Add to set for unique context count

        // Determine context status
        let contextStatus: 'completed' | 'partial' | 'not_started' = 'not_started'
        if (contextAllSubtasksCompleted) {
          contextStatus = 'completed'
        } else if (contextHasAnyResult) {
          contextStatus = 'partial'
        }

        // Calculate overdue time
        // For completed contexts, use latestCompletionTimestamp instead of currentTimestamp
        const endTimestamp =
          contextAllSubtasksCompleted && latestCompletionTimestamp > 0 ? latestCompletionTimestamp : currentTimestamp
        const overdueDays = (endTimestamp - deadlineTimestamp) / (1000 * 60 * 60 * 24)

        if (includeDetails && overdueDays > 0) {
          let unit: 'minutes' | 'hours' | 'days' = 'days'
          let displayValue = overdueDays

          if (overdueDays < 1 / 24) {
            displayValue = overdueDays * 24 * 60
            unit = 'minutes'
          } else if (overdueDays < 1) {
            displayValue = overdueDays * 24
            unit = 'hours'
          }

          // Calculate allocated time (deadline - from_ts)
          const allocatedDays =
            deadlineTimestamp > 0 && fromTimestamp > 0 ? (deadlineTimestamp - fromTimestamp) / (1000 * 60 * 60 * 24) : 0
          let allocatedUnit: 'minutes' | 'hours' | 'days' = 'days'
          let allocatedDisplayValue = allocatedDays

          if (allocatedDays < 1 / 24) {
            allocatedDisplayValue = allocatedDays * 24 * 60
            allocatedUnit = 'minutes'
          } else if (allocatedDays < 1) {
            allocatedDisplayValue = allocatedDays * 24
            allocatedUnit = 'hours'
          }

          result.overdueTasksDetails.push({
            taskId: tid,
            taskTitle: task.title || 'Untitled Task',
            contextId,
            overdueTime: overdueDays,
            unit,
            displayValue: Number(displayValue.toFixed(1)),
            contextStatus,
            allocatedTime: allocatedDays,
            allocatedUnit,
            allocatedDisplayValue: Number(allocatedDisplayValue.toFixed(1)),
            fromTs: task.from_ts || '',
            deadline: task.deadline || '',
            completedTs: contextAllSubtasksCompleted && latestCompletionTimestamp > 0 ? latestCompletionTimestamp : 0,
          })
        }
      }
    }

    // After processing all contexts for this task, add to detail arrays and metrics (once per task)

    // Determine task status (unique task, not duplicated by contexts)
    if (!taskHasAnyResult) {
      // No results at all across any context = TODO
      todoTasksSet.add(tid)
    } else if (taskIsFullyCompleted && !taskIsBlocked) {
      // All contexts fully completed and not blocked = DONE
      doneTasksSet.add(tid)
    } else if (taskIsBlocked) {
      // At least one context rejected = BLOCKED
      blockedTasksSet.add(tid)
    } else {
      // Some progress but not complete = IN_PROGRESS
      inProgressTasksSet.add(tid)
    }

    // Add completion time details if task was completed
    // Calculate completion time as: (earliest completion timestamp) - (earliest result timestamp)
    // This is for modal details only - global average is calculated per subtask above
    if (includeDetails && taskIsCompleted && taskEarliestCompletionTimestamp > 0 && taskEarliestResultTimestamp > 0) {
      const completionDays = (taskEarliestCompletionTimestamp - taskEarliestResultTimestamp) / (1000 * 60 * 60 * 24)
      if (completionDays > 0) {
        // Note: We don't add to global average here - it's calculated per subtask above

        let unit: 'minutes' | 'hours' | 'days' = 'days'
        let displayValue = completionDays

        if (completionDays < 1 / 24) {
          displayValue = completionDays * 24 * 60
          unit = 'minutes'
        } else if (completionDays < 1) {
          displayValue = completionDays * 24
          unit = 'hours'
        }

        // Calculate allocated time (deadline - from_ts)
        const allocatedDays =
          deadlineTimestamp > 0 && fromTimestamp > 0 ? (deadlineTimestamp - fromTimestamp) / (1000 * 60 * 60 * 24) : 0
        let allocatedUnit: 'minutes' | 'hours' | 'days' = 'days'
        let allocatedDisplayValue = allocatedDays

        if (allocatedDays < 1 / 24) {
          allocatedDisplayValue = allocatedDays * 24 * 60
          allocatedUnit = 'minutes'
        } else if (allocatedDays < 1) {
          allocatedDisplayValue = allocatedDays * 24
          allocatedUnit = 'hours'
        }

        result.completionTimeDetails.push({
          taskId: tid,
          taskTitle: task.title || 'Untitled Task',
          completionTime: completionDays,
          unit,
          displayValue: Number(displayValue.toFixed(1)),
          allocatedTime: allocatedDays,
          allocatedUnit,
          allocatedDisplayValue: Number(allocatedDisplayValue.toFixed(1)),
          fromTs: task.from_ts || '',
          deadline: task.deadline || '',
        })
      }
    }

    // Add to completed in time metrics (once per task, not per context)
    if (taskHasCompletionTime) {
      result.completedInTime.total++
      if (taskCompletedInTime) {
        result.completedInTime.completedInTime++
      } else {
        result.completedInTime.completedLate++
      }
    }

    // Note: overdueTasksDetails is now populated per context in the loop above
  }

  // --- POST-PROCESSING ---

  // summary.done/inProgress/todo/blocked/total are already counted during the loop (task-context pairs)
  // No need to overwrite - they represent context-level completion status

  // Set unique context counts for store completion
  result.storeCompletion.total = allContextsSet.size
  result.storeCompletion.notStarted = notStartedContextsSet.size
  result.storeCompletion.inProgress = inProgressContextsSet.size
  result.storeCompletion.allCompleted = allCompletedContextsSet.size
  result.storeCompletion.allApproved = allApprovedContextsSet.size
  result.storeCompletion.allRejected = allRejectedContextsSet.size

  // Calculate rating averages
  result.rating.averageAll = totalRatingCount > 0 ? Number((totalRatingSum / totalRatingCount).toFixed(2)) : 0
  result.rating.averageRatedOnly = ratedRatingCount > 0 ? Number((ratedRatingSum / ratedRatingCount).toFixed(2)) : 0
  result.rating.totalRatings = ratedRatingCount

  // Calculate completed in time percentage
  result.completedInTime.percentage =
    result.completedInTime.total > 0
      ? Math.round((result.completedInTime.completedInTime / result.completedInTime.total) * 100)
      : 0

  // Calculate store completed in time percentage
  result.storeCompletedInTime.percentage =
    result.storeCompletedInTime.total > 0
      ? Math.round((result.storeCompletedInTime.completedInTime / result.storeCompletedInTime.total) * 100)
      : 0

  // Calculate average completion time
  const avgCompletionTimeInDays = completionTimeCount > 0 ? completionTimeSum / completionTimeCount : 0

  // Determine the best unit to display
  let displayValue = 0
  let displayUnit: 'minutes' | 'hours' | 'days' = 'days'

  if (avgCompletionTimeInDays < 1 / 24) {
    // Less than 1 hour -> show in minutes
    displayValue = Number((avgCompletionTimeInDays * 24 * 60).toFixed(1))
    displayUnit = 'minutes'
  } else if (avgCompletionTimeInDays < 1) {
    // Less than 1 day -> show in hours
    displayValue = Number((avgCompletionTimeInDays * 24).toFixed(1))
    displayUnit = 'hours'
  } else {
    // 1 day or more -> show in days
    displayValue = Number(avgCompletionTimeInDays.toFixed(1))
    displayUnit = 'days'
  }

  result.averageCompletionTime.days = Number(avgCompletionTimeInDays.toFixed(1))
  result.averageCompletionTime.value = displayValue
  result.averageCompletionTime.unit = displayUnit
  // For now, set percentageChange to 0 (would need historical data to calculate)
  result.averageCompletionTime.percentageChange = displayValue > 0 ? -15.8 : 0

  // Calculate overdue tasks percentage change
  // For now, set percentageChange to a mock value (would need historical data to calculate)
  result.overdueTasks.percentageChange = result.overdueTasks.count > 0 ? -25.8 : 0

  // Convert skipped map to sorted array (only done once at the end)
  // Sorted by skippedCount descending - card uses maxItems to show top N, modal shows all
  result.skippedRanking = Array.from(skippedMap.entries())
    .map(([subtaskId, data]) => ({
      subtaskId,
      subtaskTitle: data.title,
      skippedCount: data.count,
      contexts: data.contexts,
    }))
    .sort((a, b) => b.skippedCount - a.skippedCount)

  // Sort overdue tasks by overdue time (descending)
  result.overdueTasksDetails.sort((a, b) => b.overdueTime - a.overdueTime)

  // Sort store completed in time by completion time (ascending - fastest first)
  result.storeCompletedInTimeDetails.sort((a, b) => a.completionTime - b.completionTime)

  // Set unique overdue tasks count
  result.overdueTasks.count = overdueTasksSet.size

  // Set overdue contexts count and percentage (already calculated in the loop)
  result.overdueContexts.count = overdueContextsSet.size
  result.overdueContexts.total = result.summary.total // total task-context pairs
  result.overdueContexts.percentage =
    result.overdueContexts.total > 0
      ? Math.round((result.overdueContexts.count / result.overdueContexts.total) * 100)
      : 0

  // --- PREPARE CONTEXT TABLE DATA ---
  // Convert contextDataMap to array with context names
  result.contextTableData = Array.from(contextDataMap.entries()).map(([contextId, data]) => {
    // Get context name using getContextName helper
    const contextName = getContextNameHelper(contextId, groupsObject, retailTags)

    // Calculate run time as time from first to last result (in days)
    const runTime =
      data.firstResultTs > 0 && data.lastResultTs > 0
        ? (data.lastResultTs - data.firstResultTs) / (1000 * 60 * 60 * 24)
        : 0

    // Quality = average score over this context's AUDITED VM subtasks (see isAuditedStatus).
    // Nothing audited yet → null, rendered as a dash. Never 0: a 0 would claim the work was judged
    // and found worthless, when in fact no one has judged it.
    const quality = data.qualityCount > 0 ? Number((data.qualitySum / data.qualityCount).toFixed(1)) : null

    // Calculate rework count
    // If context has no results at all, show null (dash)
    // Otherwise show the sum (can be 0)
    const reworkCount = data.hasAnyResult ? data.reworkCountSum : null

    return {
      contextId,
      contextName,
      tasksDone: data.tasksDone,
      tasksTotal: data.tasksTotal,
      subtasksDone: data.subtasksDone, // done + approved + rejected (NOT skipped)
      subtasksTotal: data.subtasksTotal, // ALL subtasks assigned to this context
      subtasksSkipped: data.subtasksSkipped,
      averageRank: data.ratingCount > 0 ? Number((data.ratingSum / data.ratingCount).toFixed(2)) : 0,
      rejected: data.rejected,
      approved: data.approved,
      approvedWithReservation: data.approvedWithReservation,
      reviewedByAi: data.reviewedByAi,
      notReviewed: data.notReviewed, // subtasks with status DONE from type 2 tasks
      type2SubtasksTotal: data.type2SubtasksTotal, // total subtasks from type 2 tasks
      subtasksOnTime: data.subtasksOnTime,
      subtasksLate: data.subtasksLate,
      runTime,
      quality,
      reworkCount,
    }
  })

  // --- PREPARE ALL CHART DATA (NEW) ---
  // This pre-calculates all percentages, colors, and transformations
  // so UI components only need to display data
  result.charts = {
    completionDonut: prepareCompletionDonutData(result.summary),
    storeCompletion: prepareStoreCompletionData(result.storeCompletion),
    skippedRanking: prepareSkippedRankingData(result.skippedRanking, result.completion.skipped, 3),
    reviewedStatus: prepareReviewedData(result.reviewed),
    ratingData: prepareRatingData(result.rating, result.completion.total),
  }

  return result
}

/**
 * Calculate analytics for a SINGLE task
 * Used when viewing task details
 */
export const calculateSingleTaskAnalytics = (
  task: TaskFromInsights,
  groupsObject: Record<string, IConfigGroup>,
  retailTags: ITag[]
): TaskAnalytics => {
  // Create a mock insights object with single task
  const singleInsights: TasksInsights = { temp: task }
  return calculateAggregatedAnalytics(singleInsights, groupsObject, retailTags)
}

/**
 * =============================================================================
 * SUBTASK BREAKDOWN ANALYTICS
 * =============================================================================
 * Inverted view: each row = one subtask, columns show how all contexts performed
 */

export interface SubtaskBreakdownRow {
  subtaskId: string
  subtaskTitle: string
  // Category of the subtask (from the insights `sub_tasks` object). Available for the
  // upcoming category breakdown view; not rendered by the current per-subtask table.
  category?: string
  totalContexts: number
  approved: number
  approvedWithReservation: number
  rejected: number
  notReviewed: number
  notDone: number
  inProgress: number
  done: number
  reviewedByAi: number
  skipped: number
  averageStars: number | null
  quality: number | null
  reworkCount: number
}

interface SubtaskAccumulator {
  title: string
  category?: string
  totalContexts: number
  approved: number
  approvedWithReservation: number
  rejected: number
  notReviewed: number
  notDone: number
  inProgress: number
  done: number
  reviewedByAi: number
  skipped: number
  ratingSum: number
  ratingCount: number
  qualitySum: number
  qualityCount: number
  reworkSum: number
}

// Fresh accumulator for a breakdown row (per-subtask or per-category).
const createBreakdownAccumulator = (title: string, category?: string): SubtaskAccumulator => ({
  title,
  category,
  totalContexts: 0,
  approved: 0,
  approvedWithReservation: 0,
  rejected: 0,
  notReviewed: 0,
  notDone: 0,
  inProgress: 0,
  done: 0,
  reviewedByAi: 0,
  skipped: 0,
  ratingSum: 0,
  ratingCount: 0,
  qualitySum: 0,
  qualityCount: 0,
  reworkSum: 0,
})

// Accumulate one subtask's results across all contexts into `acc`. Shared by the
// per-subtask and per-category breakdowns (they only differ by grouping key).
const accumulateSubtaskContexts = (
  acc: SubtaskAccumulator,
  task: TaskFromInsights,
  stId: string,
  contexts: string[],
  resultsLookup: ReturnType<typeof buildResultsLookup>,
  deadlineTimestamp: number
) => {
  acc.totalContexts += contexts.length

  for (let c = 0; c < contexts.length; c++) {
    const contextId = contexts[c]
    const contextResults = resultsLookup.get(contextId)
    const resultData = contextResults?.get(stId)

    if (!resultData) {
      acc.notDone++
      continue
    }

    const { status, rating, rework_count, score_deduction } = resultData

    if (status === RESULT_STATUSES.APPROVED) {
      acc.approved++
    } else if (status === RESULT_STATUSES.APPROVED_WITH_RESERVATION) {
      acc.approvedWithReservation++
    } else if (status === RESULT_STATUSES.DISAPPROVED) {
      acc.rejected++
    } else if (status === RESULT_STATUSES.DONE) {
      acc.done++
    } else if (status === RESULT_STATUSES.REVIEWED_BY_AI) {
      acc.reviewedByAi++
    } else if (status === RESULT_STATUSES.INPROGRESS) {
      acc.inProgress++
    } else if (status === RESULT_STATUSES.SKIPPED) {
      // Skipped is shown as its own "Not applicable" segment (not folded into To do)
      acc.skipped++
    } else {
      // Other unknown statuses
      acc.notDone++
    }

    if (rating != null) {
      acc.ratingSum += rating
      acc.ratingCount++
    }

    if (rework_count != null) {
      acc.reworkSum += rework_count
    }

    // Same rule as the context table: only audited results are scored, everything else is a dash.
    if (task.type === VM_TASK_TYPE && isAuditedStatus(status)) {
      acc.qualitySum += calculateResultQuality(
        status,
        score_deduction,
        rework_count,
        resultData.created_at_ts,
        deadlineTimestamp
      )
      acc.qualityCount++
    }
  }
}

export const calculateSubtaskBreakdownAnalytics = (
  insights: TasksInsights,
  groupsObject: Record<string, IConfigGroup>,
  retailTags: ITag[]
): SubtaskBreakdownRow[] => {
  const subtaskMap = new Map<string, SubtaskAccumulator>()

  const taskEntries = Object.entries(insights)

  for (let t = 0; t < taskEntries.length; t++) {
    const [taskId, task] = taskEntries[t]
    const contexts = resolveTaskContexts(task, groupsObject, retailTags)
    const resultsLookup = buildResultsLookup(task.results || [])
    const subTaskEntries = Object.entries(task.sub_tasks)
    const deadlineTimestamp = parseTimestampString(task.deadline)

    for (let s = 0; s < subTaskEntries.length; s++) {
      const [stId, stMeta] = subTaskEntries[s]
      const { title: stTitle, category: stCategory } = getInsightsSubtaskMeta(stMeta, stId)
      const subtaskKey = `${taskId}:${stId}`

      if (!subtaskMap.has(subtaskKey)) {
        subtaskMap.set(subtaskKey, createBreakdownAccumulator(stTitle, stCategory))
      }

      accumulateSubtaskContexts(subtaskMap.get(subtaskKey)!, task, stId, contexts, resultsLookup, deadlineTimestamp)
    }
  }

  const rows: SubtaskBreakdownRow[] = []

  subtaskMap.forEach((acc, subtaskKey) => {
    const averageStars = acc.ratingCount > 0 ? acc.ratingSum / acc.ratingCount : null
    const quality = acc.qualityCount > 0 ? Number((acc.qualitySum / acc.qualityCount).toFixed(1)) : null

    rows.push({
      subtaskId: subtaskKey,
      subtaskTitle: acc.title,
      category: acc.category,
      totalContexts: acc.totalContexts,
      approved: acc.approved,
      approvedWithReservation: acc.approvedWithReservation,
      rejected: acc.rejected,
      notReviewed: acc.notReviewed,
      notDone: acc.notDone,
      inProgress: acc.inProgress,
      done: acc.done,
      reviewedByAi: acc.reviewedByAi,
      skipped: acc.skipped,
      averageStars,
      quality,
      reworkCount: acc.reworkSum,
    })
  })

  rows.sort((a, b) => a.subtaskTitle.localeCompare(b.subtaskTitle))

  return rows
}

// Sentinel category key for subtasks whose `sub_tasks` object has no category.
// The component maps this to a localized "Uncategorized" label.
export const UNCATEGORIZED_CATEGORY_KEY = '__uncategorized__'

export interface CategoryBreakdownRow {
  category: string // category name, or UNCATEGORIZED_CATEGORY_KEY when absent
  subtaskCount: number // number of distinct subtasks grouped into this category
  totalContexts: number
  approved: number
  approvedWithReservation: number
  rejected: number
  notReviewed: number
  notDone: number
  inProgress: number
  done: number
  reviewedByAi: number
  skipped: number
  averageStars: number | null
  quality: number | null
  reworkCount: number
}

// Same status/quality math as calculateSubtaskBreakdownAnalytics, but rows are grouped
// by subtask category instead of by individual subtask. Powers the Categories view.
export const calculateCategoryBreakdownAnalytics = (
  insights: TasksInsights,
  groupsObject: Record<string, IConfigGroup>,
  retailTags: ITag[]
): CategoryBreakdownRow[] => {
  const categoryMap = new Map<string, SubtaskAccumulator>()
  const categorySubtaskCount = new Map<string, number>()

  const taskEntries = Object.entries(insights)

  for (let t = 0; t < taskEntries.length; t++) {
    const [, task] = taskEntries[t]
    const contexts = resolveTaskContexts(task, groupsObject, retailTags)
    const resultsLookup = buildResultsLookup(task.results || [])
    const subTaskEntries = Object.entries(task.sub_tasks)
    const deadlineTimestamp = parseTimestampString(task.deadline)

    for (let s = 0; s < subTaskEntries.length; s++) {
      const [stId, stMeta] = subTaskEntries[s]
      const { category: stCategory } = getInsightsSubtaskMeta(stMeta, stId)
      const categoryKey = stCategory && stCategory.trim() ? stCategory : UNCATEGORIZED_CATEGORY_KEY

      if (!categoryMap.has(categoryKey)) {
        categoryMap.set(categoryKey, createBreakdownAccumulator(categoryKey))
      }
      categorySubtaskCount.set(categoryKey, (categorySubtaskCount.get(categoryKey) ?? 0) + 1)

      accumulateSubtaskContexts(categoryMap.get(categoryKey)!, task, stId, contexts, resultsLookup, deadlineTimestamp)
    }
  }

  const rows: CategoryBreakdownRow[] = []

  categoryMap.forEach((acc, categoryKey) => {
    const averageStars = acc.ratingCount > 0 ? acc.ratingSum / acc.ratingCount : null
    const quality = acc.qualityCount > 0 ? Number((acc.qualitySum / acc.qualityCount).toFixed(1)) : null

    rows.push({
      category: categoryKey,
      subtaskCount: categorySubtaskCount.get(categoryKey) ?? 0,
      totalContexts: acc.totalContexts,
      approved: acc.approved,
      approvedWithReservation: acc.approvedWithReservation,
      rejected: acc.rejected,
      notReviewed: acc.notReviewed,
      notDone: acc.notDone,
      inProgress: acc.inProgress,
      done: acc.done,
      reviewedByAi: acc.reviewedByAi,
      skipped: acc.skipped,
      averageStars,
      quality,
      reworkCount: acc.reworkSum,
    })
  })

  // Uncategorized sinks to the bottom; the rest sort alphabetically.
  rows.sort((a, b) => {
    if (a.category === UNCATEGORIZED_CATEGORY_KEY) return 1
    if (b.category === UNCATEGORIZED_CATEGORY_KEY) return -1
    return a.category.localeCompare(b.category)
  })

  return rows
}

/**
 * =============================================================================
 * CHART DATA PREPARATION UTILITIES
 * =============================================================================
 * These functions transform raw analytics data into ready-to-use chart data
 * All percentage calculations and color assignments happen here
 */

/**
 * Get progress bar color based on percentage threshold
 */
export const getProgressColor = (percentage: number): string => {
  if (percentage >= 80) return '#22C55E'
  if (percentage >= 60) return '#F59E0B'
  if (percentage >= 40) return '#F97316'
  return '#EF4444'
}

/**
 * Get star color based on rating value and star position
 */
export const getStarColor = (rating: number, starNum: number): string => {
  const diff = rating - starNum
  if (diff >= 0) return '#FBBF24' // Full star
  if (diff >= -0.5) return '#FCD34D' // Half star
  return '#E5E7EB' // Empty star
}

/**
 * Contexts that reached a terminal task status. `rejected` counts as completed here: the
 * executor did submit the work, an auditor turned it down — the context is not still running.
 * Single helper so the donut center, the completion cards and the KPI row can never drift.
 */
export const getCompletedSummaryContexts = (summary: TaskAnalytics['summary']): number =>
  summary.done + summary.approved + summary.rejected

/**
 * Prepare data for Completion Status Donut Chart
 */
export const prepareCompletionDonutData = (summary: TaskAnalytics['summary']) => {
  const total = summary.total
  const completedContexts = getCompletedSummaryContexts(summary)
  const completionRate = total > 0 ? Math.round((completedContexts / total) * 100) : 0

  // Exactly the 5 task statuses — no approved-with-reservation / AI segments here.
  const items = [
    { name: TASK_STATUS_CONFIG.done.localeKey, value: summary.done, color: TASK_STATUS_CONFIG.done.bar },
    {
      name: TASK_STATUS_CONFIG.inProgress.localeKey,
      value: summary.inProgress,
      color: TASK_STATUS_CONFIG.inProgress.bar,
    },
    { name: TASK_STATUS_CONFIG.todo.localeKey, value: summary.todo, color: TASK_STATUS_CONFIG.todo.bar },
    { name: TASK_STATUS_CONFIG.approved.localeKey, value: summary.approved, color: TASK_STATUS_CONFIG.approved.bar },
    { name: TASK_STATUS_CONFIG.rejected.localeKey, value: summary.rejected, color: TASK_STATUS_CONFIG.rejected.bar },
  ]

  return {
    data: items.map((item) => ({
      ...item,
      percentage: total > 0 ? Math.round((item.value / total) * 100) : 0,
    })),
    centerValue: completedContexts,
    centerLabel: TASK_STATUS_CONFIG.done.localeKey,
    total,
    completionRate,
  }
}

/**
 * Prepare data for Store Completion Donut Chart
 */
export const prepareStoreCompletionData = (storeCompletion: TaskAnalytics['storeCompletion']) => {
  const total = storeCompletion.total

  const items = [
    {
      name: TASK_STATUS_CONFIG.completed.localeKey,
      value: storeCompletion.allCompleted,
      color: TASK_STATUS_CONFIG.completed.bar,
    },
    {
      name: TASK_STATUS_CONFIG.inProgress.localeKey,
      value: storeCompletion.inProgress,
      color: TASK_STATUS_CONFIG.inProgress.bar,
    },
    { name: TASK_STATUS_CONFIG.todo.localeKey, value: storeCompletion.notStarted, color: TASK_STATUS_CONFIG.todo.bar },
    {
      name: TASK_STATUS_CONFIG.approved.localeKey,
      value: storeCompletion.allApproved,
      color: TASK_STATUS_CONFIG.approved.bar,
    },
    {
      name: TASK_STATUS_CONFIG.rejected.localeKey,
      value: storeCompletion.allRejected,
      color: TASK_STATUS_CONFIG.rejected.bar,
    },
  ]

  return {
    data: items.map((item) => ({
      ...item,
      percentage: total > 0 ? Math.round((item.value / total) * 100) : 0,
    })),
    total,
  }
}

/**
 * Prepare data for Skipped Subtasks Chart
 */
export const prepareSkippedRankingData = (
  skippedRanking: TaskAnalytics['skippedRanking'],
  totalSkipped: number,
  maxItems: number = 3
) => {
  const displayData = skippedRanking.slice(0, maxItems)
  const maxValue = Math.max(...displayData.map((d) => d.skippedCount), 1)

  return {
    data: displayData.map((item) => ({
      name: item.subtaskTitle,
      value: item.skippedCount,
      color: '#F97316',
      percentage: maxValue > 0 ? Math.round((item.skippedCount / maxValue) * 100) : 0,
    })),
    maxValue,
    totalSkipped,
  }
}

/**
 * Prepare data for Reviewed Status Chart (Type 2 Tasks)
 */
export const prepareReviewedData = (reviewed: TaskAnalytics['reviewed']) => {
  const total = reviewed.total

  const items = [
    { name: TASK_STATUS_CONFIG.approved.localeKey, value: reviewed.approved, color: TASK_STATUS_CONFIG.approved.bar },
    {
      name: TASK_STATUS_CONFIG.approvedWithReservation.localeKey,
      value: reviewed.approvedWithReservation,
      color: TASK_STATUS_CONFIG.approvedWithReservation.bar,
    },
    { name: TASK_STATUS_CONFIG.rejected.localeKey, value: reviewed.rejected, color: TASK_STATUS_CONFIG.rejected.bar },
    {
      name: TASK_STATUS_CONFIG.reviewedByAi.localeKey,
      value: reviewed.reviewedByAi,
      color: TASK_STATUS_CONFIG.reviewedByAi.bar,
    },
    {
      name: TASK_STATUS_CONFIG.notReviewed.localeKey,
      value: reviewed.notReviewed,
      color: TASK_STATUS_CONFIG.notReviewed.bar,
    },
  ]

  return {
    data: items.map((item) => ({
      ...item,
      percentage: total > 0 ? Math.round((item.value / total) * 100) : 0,
    })),
    total,
  }
}

/**
 * Prepare data for Rating Chart
 */
export const prepareRatingData = (rating: TaskAnalytics['rating'], totalSubtasks: number) => {
  const unratedCount = totalSubtasks - rating.totalRatings
  const maxDistribution = Math.max(...Object.values(rating.ratingDistribution), unratedCount || 0, 1)

  const distribution = [1, 2, 3, 4, 5].map((starNum) => ({
    stars: starNum,
    count: rating.ratingDistribution[starNum] || 0,
    percentage: maxDistribution > 0 ? Math.round((rating.ratingDistribution[starNum] / maxDistribution) * 100) : 0,
    color: getStarColor(rating.averageRatedOnly, starNum),
  }))

  return {
    averageRating: rating.averageRatedOnly,
    totalRatings: rating.totalRatings,
    distribution,
    maxDistribution,
    unratedCount,
    unratedPercentage: maxDistribution > 0 ? Math.round((unratedCount / maxDistribution) * 100) : 0,
  }
}
